import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'

import { InputAdornment, TextField, TextFieldProps } from '@mui/material'
import { ReactElement, ReactNode, useState } from 'react'

export type TAdditionalNumericInputProps = {
    initValue?: number
    unit?: ReactElement | string
    step: number
    min?: number
    max?: number
    minRecomended?: number
    maxRecomended?: number
    errorOnEmptyInit?: boolean
    onNumberChanged?: (newValue: number, event: React.ChangeEvent<HTMLInputElement>) => void
}

export type TNumericInputProps = TextFieldProps & TAdditionalNumericInputProps

type TFieldCondition = {
    error: boolean
    helperText?: ReactNode
    startAdormnet?: ReactNode
}

function getDecimalPlaces(value: number) {
    const text = value.toString()
    const index = text.indexOf('.')

    if (index === -1) {
        return 0
    }

    return text.length - index - 1
}

function isStepValid(value: number, step: number, min = 0) {
    const precision = Math.max(
        getDecimalPlaces(value),
        getDecimalPlaces(step),
        getDecimalPlaces(min)
    )

    const scale = Math.pow(10, precision)

    const scaledValue = Math.round((value - min) * scale)
    const scaledStep = Math.round(step * scale)

    return scaledStep !== 0 && scaledValue % scaledStep === 0
}

export default function NumericInput(props: TNumericInputProps) {
    const [inputBuffer, setInputBuffer] = useState(props.initValue !== undefined ? props.initValue.toString() : '')
    const [fieldCondition, setFieldCondition] = useState<TFieldCondition>(
        props.errorOnEmptyInit && inputBuffer === ''
            ? { error: true, helperText: 'Please enter the data', startAdormnet: <ErrorOutlineOutlinedIcon fontSize='small' color='error' /> }
            : { error: false }
    )

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputBuffer(event.target.value)

        const fieldValue = Number.parseFloat(event.target.value)

        if (Number.isNaN(fieldValue)) {
            setFieldCondition({
                error: true,
                helperText: `Invalid input.`,
                startAdormnet: <ErrorOutlineOutlinedIcon fontSize='small' color='error' />
            })
            return
        }

        let newFieldCondition: TFieldCondition = { error: false }

        if (props.min !== undefined && fieldValue < props.min) {
            newFieldCondition = {
                error: true,
                helperText: <>{`The value is less than allowed minimum ${props.min} `} {props.unit}.</>,
                startAdormnet: <WarningAmberOutlinedIcon fontSize='small' color='error' />
            }
        } else if (props.max !== undefined && fieldValue > props.max) {
            newFieldCondition = {
                error: true,
                helperText: <>{`The value is greater than allowed maximum ${props.max} `} {props.unit}.</>,
                startAdormnet: <ErrorOutlineOutlinedIcon fontSize='small' color='error' />
            }
        } else if (props.minRecomended !== undefined && fieldValue < props.minRecomended) {
            newFieldCondition = {
                error: false,
                helperText: props.maxRecomended
                    ? <>{`Recommended value is between ${props.minRecomended} and ${props.maxRecomended} `} {props.unit}.</>
                    : <>{`Recommended value is greater than ${props.minRecomended} `} {props.unit}.</>,
                startAdormnet: <WarningAmberOutlinedIcon fontSize='small' color='warning' />
            }
        } else if (props.maxRecomended !== undefined && fieldValue > props.maxRecomended) {
            newFieldCondition = {
                error: false,
                helperText: props.minRecomended
                    ? <>{`Recommended value is between ${props.minRecomended} and ${props.maxRecomended} `} {props.unit}.</>
                    : <>{`Recommended value is less than ${props.maxRecomended} `} {props.unit}. </>,
                startAdormnet: <WarningAmberOutlinedIcon fontSize='small' color='warning' />
            }
        }

        if (!isStepValid(fieldValue, props.step, props.min ?? 0)) {
            newFieldCondition = {
                error: true,
                helperText: `Should be divisible by ${props.step}.`,
                startAdormnet: <WarningAmberOutlinedIcon fontSize='small' color='warning' />
            }
        }

        setFieldCondition(newFieldCondition)

        if (props.onNumberChanged) {
            props.onNumberChanged(fieldValue, event)
        }
    }

    const adjustedProps = { ...props }
    delete adjustedProps.minRecomended
    delete adjustedProps.maxRecomended
    delete adjustedProps.onNumberChanged
    delete adjustedProps.initValue
    delete adjustedProps.errorOnEmptyInit

    return (
        <TextField type="number"
            variant='standard'
            {...adjustedProps}
            value={inputBuffer}
            onChange={handleChange}
            InputProps={{
                endAdornment: <InputAdornment position="end">{props.unit}</InputAdornment>,
                startAdornment: fieldCondition.startAdormnet
            }}
            error={fieldCondition.error}
            helperText={fieldCondition.helperText}
            inputProps={{
                style: { textAlign: 'right' },
                maxLength: 130,
                step: props.step,
                min: props.min,
                max: props.max,
            }} />
    )
}